import React, { useState, useContext, useEffect } from 'react';
import { ArchiveExportContext } from '../context/ArchiveExportContext';
import { AuthContext } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import useGradebookExport from '../hooks/useGradebookExport';

const ArchiveExport = () => {
  const { 
    archiveStatus, 
    checkCourseVersion,
    archiveCourseData, 
    exportData,
    exportAllArchivedData,
    checkArchiveTablesExist,
    getArchiveHistory,
    clearStatus,
    clearStudentData,
    deleteArchive
  } = useContext(ArchiveExportContext);
  
  const { isInstructor, isTA } = useContext(AuthContext);
  
  const [archiveHistory, setArchiveHistory] = useState([]);
  const [hasArchiveTables, setHasArchiveTables] = useState(false);
  const [courseVersion, setCourseVersion] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [archiveStep, setArchiveStep] = useState('input'); // 'input', 'confirm', 'warning'
  const [versionCheckResult, setVersionCheckResult] = useState(null);
  const [showClearWarning, setShowClearWarning] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(null); // null or { courseVersion }
  const {
    canExport: canExportGradebook,
    loading: loadingGradebook,
    loadingStudents: loadingGradebookStudents,
    loadingGrades: loadingGradebookGrades,
    error: gradebookError,
    hasStudents: gradebookHasStudents,
    hasColumns: gradebookHasColumns,
    downloadGradebook,
  } = useGradebookExport();

  // Load archive history on component mount
  useEffect(() => {
    const loadData = async () => {
      // Only load if user has permission
      if (!isInstructor && !isTA) return;
      
      try {
        const [history, hasArchives] = await Promise.all([
          getArchiveHistory(),
          checkArchiveTablesExist()
        ]);
        setArchiveHistory(history);
        setHasArchiveTables(hasArchives);
      } catch (error) {
        console.error('Failed to load archive data:', error);
      }
    };
    loadData();
  }, [getArchiveHistory, checkArchiveTablesExist, isInstructor, isTA]);

  // Check if user has permission to access this component
  if (!isInstructor && !isTA) {
    return (
      <div className="archive-export-container">
        <div className="error-message">
          Access Denied: Only instructors and teaching assistants can access archive and export functions.
        </div>
      </div>
    );
  }

  // Validate course version name
  const validateCourseVersion = (version) => {
    const versionRegex = /^[a-zA-Z0-9_-]+$/;
    return version && versionRegex.test(version);
  };

  // Handle course version input
  const handleCourseVersionSubmit = async () => {
    if (!validateCourseVersion(courseVersion)) {
      // Use a temporary error display since we can't directly set the status
      alert('Course version must contain only letters, numbers, hyphens, and underscores (no spaces)');
      return;
    }

    try {
      const checkResult = await checkCourseVersion(courseVersion);
      setVersionCheckResult(checkResult);

      if (checkResult.exists) {
        setArchiveStep('warning');
      } else {
        setArchiveStep('confirm');
      }
    } catch (error) {
      console.error('Error checking course version:', error);
    }
  };

  // Handle archive confirmation
  const handleArchiveConfirm = async (overwrite = false) => {
    try {
      await archiveCourseData(courseVersion, overwrite);
      
      // Refresh data after successful archive
      const [history, hasArchives] = await Promise.all([
        getArchiveHistory(),
        checkArchiveTablesExist()
      ]);
      setArchiveHistory(history);
      setHasArchiveTables(hasArchives);
      
      // Reset form
      setCourseVersion('');
      setArchiveStep('input');
      setVersionCheckResult(null);
    } catch (error) {
      console.error('Archive failed:', error);
    }
  };

  // Handle data export
  const handleExportData = async (dataType) => {
    try {
      await exportData(dataType, exportFormat);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Handle bulk archive export
  const handleExportAllArchived = async () => {
    try {
      await exportAllArchivedData(exportFormat);
    } catch (error) {
      console.error('Bulk archive export failed:', error);
    }
  };

  // Handle clear data click (show warning)
  const handleClearDataClick = () => {
    setShowClearWarning(true);
  };

  // Handle clear data confirmation
  const handleClearDataConfirm = async () => {
    try {
      await clearStudentData();
      setShowClearWarning(false);
    } catch (error) {
      console.error('Clear data failed:', error);
    }
  };

  // Cancel clear data
  const handleClearDataCancel = () => {
    setShowClearWarning(false);
  };

  // Handle delete archive click (show warning)
  const handleDeleteArchiveClick = (courseVersion) => {
    setDeleteWarning({ courseVersion });
  };

  // Handle delete archive confirmation
  const handleDeleteArchiveConfirm = async () => {
    if (!deleteWarning) return;
    
    try {
      await deleteArchive(deleteWarning.courseVersion);
      
      // Refresh data after successful deletion
      const [history, hasArchives] = await Promise.all([
        getArchiveHistory(),
        checkArchiveTablesExist()
      ]);
      setArchiveHistory(history);
      setHasArchiveTables(hasArchives);
      
      setDeleteWarning(null);
    } catch (error) {
      console.error('Delete archive failed:', error);
    }
  };

  // Cancel delete archive
  const handleDeleteArchiveCancel = () => {
    setDeleteWarning(null);
  };

  // Reset archive form
  const resetArchiveForm = () => {
    setCourseVersion('');
    setArchiveStep('input');
    setVersionCheckResult(null);
    clearStatus();
  };

  return (
    <div className="archive-export-container">
      

      {/* Status Messages */}
      {(archiveStatus.message || archiveStatus.error) && (
        <div className={`status-message ${archiveStatus.error ? 'error' : 'success'}`}>
          {archiveStatus.error || archiveStatus.message}
          <button 
            className="clear-status-btn"
            onClick={clearStatus}
            aria-label="Clear status message"
          >
            ×
          </button>
        </div>
      )}

      {/* Archive Section */}
      <div className="archive-section">
        <h3>Archive Course Data</h3>
        <p className="archive-description">
          At the end of the semester, you can archive all of the data from that instance of the course. This includes course design data, student answers, and grades. The data is saved under a name for that version of the course (e.g., "fall25") that you provide. None of the current data is deleted. (You can clear existing student answers and grades below.)
        </p>

        {archiveStep === 'input' && (
          <div className="archive-input-step">
            <div className="course-version-input">
              <label htmlFor="course-version">
                Course Version Name:
                <input
                  id="course-version"
                  type="text"
                  value={courseVersion}
                  onChange={(e) => setCourseVersion(e.target.value)}
                  placeholder="e.g., fall25, spring-2024, summer_intensive"
                  className="version-input"
                />
              </label>
              <p className="input-help">
                Use letters, numbers, hyphens, and underscores only (no spaces).
              </p>
            </div>
            
            <div className="archive-buttons">
              <button
                className="archive-btn primary"
                onClick={handleCourseVersionSubmit}
                disabled={!courseVersion || archiveStatus.isArchiving}
              >
                Continue to Archive
              </button>
            </div>
          </div>
        )}

        {archiveStep === 'confirm' && (
          <div className="archive-confirm-step">
            <div className="confirm-message">
              <h4>Ready to Archive</h4>
              <p>
                You are about to archive all course data with the version name: <strong>"{courseVersion}"</strong>
              </p>
              <p>This will create archive copies of:</p>
              <ul>
                <li>Student answers and submissions</li>
                <li>Student grades and evaluations</li>
                <li>Course design and configuration</li>
                <li>User profiles and permissions</li>
              </ul>
              <p><em>Original data will remain unchanged in the active course.</em></p>
            </div>
            
            <div className="archive-buttons">
              <button
                className="archive-btn primary"
                onClick={() => handleArchiveConfirm(false)}
                disabled={archiveStatus.isArchiving}
              >
                {archiveStatus.isArchiving ? 'Archiving...' : 'Archive Course Data'}
              </button>
              <button
                className="archive-btn secondary"
                onClick={resetArchiveForm}
                disabled={archiveStatus.isArchiving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {archiveStep === 'warning' && versionCheckResult && (
          <div className="archive-warning-step">
            <div className="warning-message">
              <h4>⚠️ Version Already Exists</h4>
              <p>
                The course version <strong>"{courseVersion}"</strong> has already been archived.
              </p>
              <p>Would you like to overwrite the existing archive with new data?</p>
              <p><em>This will replace all previously archived data for this version.</em></p>
            </div>
            
            <div className="archive-buttons">
              <button
                className="archive-btn warning"
                onClick={() => handleArchiveConfirm(true)}
                disabled={archiveStatus.isArchiving}
              >
                {archiveStatus.isArchiving ? 'Overwriting...' : 'Overwrite Existing Archive'}
              </button>
              <button
                className="archive-btn secondary"
                onClick={resetArchiveForm}
                disabled={archiveStatus.isArchiving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Archive History */}
      <div className="history-section">
        <h3>Archive History</h3>
        <div className="history-content">
          {archiveHistory.length === 0 ? (
            <p className="no-history">No archive history available.</p>
          ) : (
            <div className="history-list">
              {archiveHistory.map((entry, index) => (
                <div 
                  key={index} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 15px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{
                    flex: 1,
                    color: '#495057',
                    fontSize: '14px'
                  }}>
                    {entry.courseVersion} ({new Date(entry.date).toLocaleDateString()})
                  </span>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color2)',
                      cursor: 'pointer',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      minWidth: '20px',
                      height: '20px',
                      opacity: archiveStatus.isDeleting ? 0.5 : 1
                    }}
                    onClick={() => handleDeleteArchiveClick(entry.courseVersion)}
                    disabled={archiveStatus.isDeleting}
                    title="Delete this archive"
                    onMouseEnter={(e) => {
                      if (!archiveStatus.isDeleting) {
                        e.target.style.backgroundColor = 'var(--color1)';
                        e.target.style.color = 'white';
                        e.target.style.transform = 'scale(1.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!archiveStatus.isDeleting) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--color2)';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Archive Warning Dialog */}
        {deleteWarning && (
          <div className="delete-warning-dialog">
            <div className="warning-message">
              <h4>⚠️ Warning: Delete Archive</h4>
              <p>
                You are about to permanently delete the archive for course version: <strong>"{deleteWarning.courseVersion}"</strong>
              </p>
              <p>This will delete:</p>
              <ul>
                <li>All archived student data for this version</li>
                <li>All archived course design data for this version</li>
                <li>All archived user data for this version</li>
                <li>The archive log entry</li>
              </ul>
              <p><strong>This action cannot be undone.</strong></p>
            </div>
            
            <div className="warning-buttons">
              <button
                className="delete-confirm-btn danger"
                onClick={handleDeleteArchiveConfirm}
                disabled={archiveStatus.isDeleting}
              >
                {archiveStatus.isDeleting ? 'Deleting...' : `Yes, Delete "${deleteWarning.courseVersion}" Archive`}
              </button>
              <button
                className="delete-cancel-btn secondary"
                onClick={handleDeleteArchiveCancel}
                disabled={archiveStatus.isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Clear Student Data */}
      <div className="clear-data-section">
        <h3>Clear Student Data</h3>
        <p className="clear-data-description">
          Before a new semester, you should clear existing student answers and grading data from the course. 
          This will not affect any elements of the course design. If you want to save this data, you can 
          archive it (above) or download it (below).
        </p>
        
        <div className="clear-data-buttons">
          <button
            className="clear-data-btn warning"
            onClick={handleClearDataClick}
            disabled={archiveStatus.isClearing}
          >
            {archiveStatus.isClearing ? 'Clearing Data...' : 'Clear Student Data'}
          </button>
        </div>

        {/* Clear Data Warning Dialog */}
        {showClearWarning && (
          <div className="clear-warning-dialog">
            <div className="warning-message">
              <h4>⚠️ Warning: Clear Student Data</h4>
              <p>
                You are about to permanently delete all student data from this course, including:
              </p>
              <ul>
                <li>All student answers and submissions</li>
                <li>All student grades and evaluations</li>
                <li>All student answer history</li>
                <li>All student user profile data</li>
              </ul>
              <p><strong>This action cannot be undone.</strong></p>
              <p><em>Instructor data and course design will not be affected.</em></p>
            </div>
            
            <div className="warning-buttons">
              <button
                className="clear-confirm-btn danger"
                onClick={handleClearDataConfirm}
                disabled={archiveStatus.isClearing}
              >
                {archiveStatus.isClearing ? 'Clearing...' : 'Yes, Clear All Student Data'}
              </button>
              <button
                className="clear-cancel-btn secondary"
                onClick={handleClearDataCancel}
                disabled={archiveStatus.isClearing}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Options */}
      {canExportGradebook && (
        <div className="gradebook-export-section">
          <h3>Export Gradebook</h3>
          <p className="gradebook-export-description">
            Export student grades on course evaluations into Excel format.
          </p>
          {gradebookError && <div className="gradebook-export-error">{gradebookError}</div>}
          <button
            type="button"
            className="gradebook-export-button"
            onClick={downloadGradebook}
            disabled={loadingGradebook || !gradebookHasStudents || !gradebookHasColumns}
          >
            {loadingGradebookStudents || loadingGradebookGrades ? 'Preparing…' : 'Download grades (.xlsx)'}
          </button>
          {!gradebookHasStudents && !loadingGradebookStudents && !gradebookError && (
            <p className="gradebook-export-hint">No student data available yet for this course.</p>
          )}
        </div>
      )}

      <div className="export-section">
        <h3>Export Raw Data</h3>

        <p className="clear-data-description">
          Using the options below, you can export various types of raw course data into different
          formats. You can do this any time during the semester; the information stored in the
          database won't be affected.
        </p>
        
        <div className="export-format">
          <label htmlFor="export-format">
            Export Format:
            <select
              id="export-format"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="format-select"
            >
              <option value="xlsx">Excel</option>
              <option value="csv">CSV</option>
            </select>
          </label>
        </div>

        <div className="export-buttons">
          <button
            className="export-btn"
            style={{ margin: '8px' }}
            onClick={() => handleExportData('studentAnswers')}
            disabled={archiveStatus.isExporting}
          >
            {archiveStatus.isExporting ? 'Exporting...' : 'Export Student Answers'}
          </button>

          <button
            className="export-btn"
            style={{ margin: '8px' }}
            onClick={() => handleExportData('studentAnswersHistory')}
            disabled={archiveStatus.isExporting}
          >
            {archiveStatus.isExporting ? 'Exporting...' : 'Export Student Answer History'}
          </button>

          <button
            className="export-btn"
            style={{ margin: '8px' }}
            onClick={() => handleExportData('studentGrades')}
            disabled={archiveStatus.isExporting}
          >
            {archiveStatus.isExporting ? 'Exporting...' : 'Export Student Grades'}
          </button>

          <button
            className="export-btn"
            style={{ margin: '8px' }}
            onClick={() => handleExportData('courseDesign')}
            disabled={archiveStatus.isExporting}
          >
            {archiveStatus.isExporting ? 'Exporting...' : 'Export Course Design'}
          </button>

          <button
            className="export-btn"
            style={{ margin: '8px' }}
            onClick={() => handleExportData('userProfiles')}
            disabled={archiveStatus.isExporting}
          >
            {archiveStatus.isExporting ? 'Exporting...' : 'Export User Profiles'}
          </button>

          {/* Bulk Export Button - only show if there are archives */}
          {hasArchiveTables && (
            <button
              className="export-btn"
              style={{ margin: '8px' }}
              onClick={handleExportAllArchived}
              disabled={archiveStatus.isExporting}
            >
              {archiveStatus.isExporting ? 'Exporting...' : 'Export All Archived Data'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchiveExport;
